import { useEffect, useState } from "react";
import { ClipboardCheck, FileDown, RotateCcw } from "lucide-react";
import { API } from "../../lib/api";
import { Panel, Cta } from "./_ui";

const SECTIONS = [
  ["File format", ["Single PDF (PDF/X-4 or X-1a), one file per product", "Fonts embedded or outlined", "Images embedded, not linked"]],
  ["Size, bleed & safe zone", ["Page = trim + bleed (0.125\"; 0.25\" large format)", "Backgrounds extend into the bleed", "Text & logos ≥ 0.125\" inside trim (1\" on banners)"]],
  ["Colour", ["Document in CMYK — no RGB left", "Rich black C60 M40 Y40 K100 for solids; 100K for small type", "Spot colours named by Pantone (C or U); unused swatches deleted", "Total ink under 300%", "White ink / foil / spot UV on their own named spot layers"]],
  ["Resolution", ["Photos 300 dpi at size (150 min); large format 100–150 dpi", "Logos and type as vector", "Line art at 1200 dpi"]],
  ["Type & lines", ["Type ≥ 6 pt (7 pt reversed, 8 pt on kraft)", "Lines ≥ 0.25 pt positive, 0.5 pt reversed", "No registration black in text"]],
  ["Layout", ["Booklet pages a multiple of 4, single pages in order", "Fold panels sized for fold-in", "Back oriented correctly to front", "Dieline on a DIELINE spot layer, stroke only, overprint"]],
  ["Before upload", ["Spell-check names, numbers, URLs", "Acrobat Output Preview: separations & ink coverage", "Compared against the Kolt template", "Special instructions noted in the order"]],
];
const KEY = "kolt.prepress.checklist";

export function PrepressChecklist() {
  const [done, setDone] = useState(() => { try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; } });
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(done)); }, [done]);
  const total = SECTIONS.reduce((s, [, i]) => s + i.length, 0);
  const count = Object.values(done).filter(Boolean).length;
  const toggle = (k) => setDone((d) => ({ ...d, [k]: !d[k] }));

  return (
    <Panel icon={ClipboardCheck} title="Interactive prepress checklist" testId="prepress-checklist">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-40 h-2 bg-black/40 rounded-full overflow-hidden"><div className="h-full bg-[#B87333] transition-all" style={{ width: `${(count / total) * 100}%` }} /></div>
          <span className="font-mono text-xs text-white/70" data-testid="checklist-progress">{count} / {total} checked</span>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setDone({})} className="inline-flex items-center gap-1 font-mono text-[10px] tracking-widest uppercase text-white/60 hover:text-white" data-testid="checklist-reset"><RotateCcw className="w-3 h-3" /> Reset</button>
          <a href={`${API}/tools/prepress-checklist.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-white/25 hover:border-[#B87333] font-mono text-[10px] tracking-widest uppercase font-bold px-3 py-1.5" data-testid="checklist-pdf"><FileDown className="w-3.5 h-3.5" /> Download PDF</a>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
        {SECTIONS.map(([title, items]) => (
          <div key={title}>
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#E8B587] font-bold mb-1.5">{title}</div>
            <ul className="space-y-1">
              {items.map((it) => { const k = `${title}:${it}`; return (
                <li key={k}>
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={!!done[k]} onChange={() => toggle(k)} className="mt-0.5 accent-[#B87333]" data-testid={`checklist-item-${k.replace(/\W+/g, "-").slice(0, 40)}`} />
                    <span className={done[k] ? "line-through text-white/40" : ""}>{it}</span>
                  </label>
                </li>
              ); })}
            </ul>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-5">
        <Cta to="/quote?topic=design-assist" testId="checklist-cta-preflight">Upload your file for a free preflight</Cta>
        <Cta to="#dpi-checker" outline testId="checklist-cta-dpi">Check image resolution first</Cta>
      </div>
    </Panel>
  );
}
